import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { startCase } from "lodash";
import { INVOICE_STATUS } from "@/constants";
import { updateInvoiceStatus } from "@/store/slices/invoiceSlice";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { AppDispatch } from "@/store/store";

// Draft is excluded - draft invoices can't have their status updated from the list.
const statusOptions = Object.values(INVOICE_STATUS).filter((status) => status !== INVOICE_STATUS.draft);

interface InvoiceStatusCellProps {
  params: GridRenderCellParams;
  invoiceId: string;
  onStatusUpdated: (args: { id: string; status: string }) => void;
}

export default function InvoiceStatusCell({ params, invoiceId, onStatusUpdated }: InvoiceStatusCellProps) {
  const dispatch = useDispatch<AppDispatch>();
  const [value, setValue] = useState<string>(String(params.row.status).toLowerCase());

  useEffect(() => {
    setValue(String(params.row.status).toLowerCase());
  }, [params.row.status]);

  // Draft invoices can't have their status changed from the list - show plain text.
  if (value === INVOICE_STATUS.draft) {
    return <>{startCase(params.row.status)}</>;
  }

  const handleStatusUpdate = async (event: SelectChangeEvent) => {
    const newStatus = event.target.value;
    const previousStatus = value;
    setValue(newStatus);

    const res = await dispatch(updateInvoiceStatus({ invoiceId, status: newStatus }));

    if ((res as { error?: unknown })?.error) {
      setValue(previousStatus);
      dispatch(openSnackbar({ message: "Failed to update invoice status", intent: "danger" }));
      return;
    }

    onStatusUpdated({ id: invoiceId, status: newStatus });
    dispatch(openSnackbar({ message: "Invoice status updated successfully", intent: "success" }));
  };

  return (
    <Select
      id={`${invoiceId}-invoice-status-select`}
      value={value}
      onChange={handleStatusUpdate}
      onClick={(e) => e.stopPropagation()}
      variant="standard"
      disableUnderline
      fullWidth
      sx={{
        fontSize: "14px",
        "& .MuiSelect-select:focus": { backgroundColor: "transparent" },
      }}
    >
      {statusOptions.map((status) => (
        <MenuItem value={status} key={status}>
          {startCase(status)}
        </MenuItem>
      ))}
    </Select>
  );
}
